import { Subject } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { Message, MessageService, MessageType } from './message.service';
import { Product, Order, Money, OrderType, PaymentMethod } from '../lib/lib';
import { IVuConnection } from './vu/connection/vu-connection.interfaces';
import { IVuHttp } from './vu/http/vu-http.interface';
import { LoggingService } from './logging/logging.service';
import { DispatcherService } from './dispatcher.service';
import { VuCommunicationService } from './vu/vu-communication.service';
import { BsModalRef } from 'ngx-bootstrap';
import { TicketParameters } from '../lib/ticket/ticket-parameters';
import { TicketParametersComponent } from '../components/general/ticket-parameters/ticket-parameters.component';
import { CreditCardMessageStore } from '../lib/credit-card/credit-card-message-store';
import { RemotePaymentTransaction } from '../lib/remote-payment-transaction';
import { DisplayItem } from '../lib/display/display-item/display-item';
import { ModalService } from './gui/modal/modal-service';

@Injectable()
export class SaleService {
  private internalOrder: Order;
  private vuConnection: IVuConnection;
  private vuHttp: IVuHttp;
  private log: LoggingService;
  private dispatcherService: DispatcherService;
  private vuCommunicationService: VuCommunicationService;
  private modalRef: BsModalRef;
  protected messageService: MessageService;
  eventAmountToPayReached: Subject<Money> = new Subject();
  eventMoneyChanged: Subject<Money> = new Subject();
  creditCardMessageStore = new CreditCardMessageStore();
  private internalRemoteTransaction: RemotePaymentTransaction;

  constructor(
    protected injector: Injector,
    private modalService: ModalService,
  ) {
    this.log = this.injector.get(LoggingService);
    this.dispatcherService = this.injector.get(DispatcherService);
    this.vuCommunicationService = this.injector.get(VuCommunicationService);
    this.messageService = this.injector.get(MessageService);
    this.vuConnection = this.vuCommunicationService.vuConnection;
    this.vuHttp = this.vuCommunicationService.vuHttp;
    this.internalRemoteTransaction = new RemotePaymentTransaction(this.vuHttp, this.log);
    this.resetOrder();
    this.dispatcherService.onVisualItemLeafSelectedSubscribe(x => this.onVisualItemLeafSelected(x));
    this.vuConnection.eventMoneyChanged.subscribe((x: Money) => this.onMoneyChanged(x));
    this.messageService.getSubscription().subscribe(message => {
      if (message && message.messageType === MessageType.MachineTimeoutModalCancel) {
        this.modalService.close(this.modalRef);
      }
    });
  }

  get remoteTransaction(): RemotePaymentTransaction {
    return this.internalRemoteTransaction;
  }

  onVisualItemLeafSelected(message: Message) {
    if (!message || !message.info) {
      return;
    }

    let product = message.info.data as Product;
    if (!product) {
      const displayItem = message.info as DisplayItem;
      if (displayItem) {
        product = displayItem.product;
      }
    }

    if (!Product.isProduct(product)) {
      return;
    }

    if (product.isPartnerRequired || (product.groupSizeMin > 0 && product.groupSizeMax > 0) || product.appointmentEnabled) {
      if (this.modalRef) {
        return;
      }

      const ticketParameters = new TicketParameters();
      ticketParameters.groupSize = product.groupSizeMin;

      const data = {
        ticketParameters,
        product
      };

      this.modalRef = this.modalService.show(
        TicketParametersComponent,
        data,
        (accepted: boolean) => {
          this.modalRef = null;
          if (accepted) {
            this.order.addProduct(
              product,
              1,
              undefined,
              undefined,
              ticketParameters
            );
          } else {
            this.dispatcherService.onBackButtonClick();
          }
        }
      );
    } else {
      this._addProduct(product);
    }
  }

  private _addProduct(product: Product) {
    const line = this.order.addProduct(product, 1);

    const additionalProductIds = product.additionalProductIds;
    if (!additionalProductIds || additionalProductIds.length === 0) {
      return;
    }

    if (line.minQuantity === 0) {
      line.minQuantity = 1;
    }

    this.vuHttp.getProductsByIds(additionalProductIds)
      .then(products => {
        products.forEach(item => {
          if (additionalProductIds.includes(item.id)) {
            const orderLine = this.order.addProduct(item, 0);
            orderLine.required = true;
          }
        });
      });
  }

  onMoneyChanged(money: Money) {
    this.log.info(`SaleService. onMoneyChanged. '${money}'`);
    this.order.registerMoney(money);
    this.eventMoneyChanged.next(money);
  }

  resetOrder() {
    this.log.info('SaleService. Order reset.');
    this.internalOrder = new Order();
    this.dispatcherService.shopStateOrderUid(this.order.uid);
    this.remoteTransaction.reset();
  }

  ticketReturnAmount(product: Product, amount: Money, orderId: number, barcode: string, isDisplayMode: boolean) {
    if (!isDisplayMode) {
      this.resetOrder();
    }

    this.log.info(`SaleService. ticketReturnAmount. product. ${product}`);
    this.log.info(`SaleService. ticketReturnAmount. amount: ${amount}. orderId: ${orderId}. barcode: ${barcode}.`);
    const order = this.order;
    order.originalOrderId = orderId;
    order.type = OrderType.SaleRefund;
    const negateAmount = amount.negate();
    order.addProduct(product, 1, negateAmount, barcode);
    this.log.info(`SaleService. ticketReturnAmount. order. ${order}`);
    this.dispatcherService.ticketReturnAmount(isDisplayMode);
  }

  get order() {
    return this.internalOrder;
  }

  openPaymentSession(paymentMethod: PaymentMethod, amount?: Money) {
    this.log.info(`SaleService. openPaymentSession. paymentMethod: '${PaymentMethod[paymentMethod]}'. amount: '${amount}'.`);
    this.order.paymentMethod = paymentMethod;
    this.remoteTransaction.openRemoteTransaction(paymentMethod, amount);
    this.order.openSession();
  }

  openPaymentSessionWithFloatingAmount(paymentMethod: PaymentMethod, minAmount: Money, maxAmount: Money) {
    this.log.info('SaleService. openPaymentSessionWithFloatingAmount.' +
      `paymentMethod: '${PaymentMethod[paymentMethod]}'. minAmount: '${minAmount}'. maxAmount: '${maxAmount}'.`);
    this.order.paymentMethod = paymentMethod;
    const amount = maxAmount;
    this.remoteTransaction.openRemoteTransaction(paymentMethod, amount, true);
    this.order.openSession(minAmount, maxAmount);
  }

  closePaymentSession() {
    this.order.closeSession();
    this.resetOrder();
  }

  get paymentSession() {
    return this.order.paymentSession;
  }

  get orderId(): number {
    return this.order == null ? -1 : this.order.id;
  }

  get buyOrderInOneClick(): boolean {
    if (!this.order || this.order.orderLines.length !== 1 || !this.order.orderLines[0].product) {
      return false;
    }

    return this.order.orderLines[0].product.buyInOneClick;
  }

  get orderInProgress(): boolean {
    return this.order && !!this.order.orderLines;
  }
}
